import * as React from "react";

import { cn } from "../../lib/utils";

/**
 * Textarea component provides a styled multi-line text input field with
 * consistent styling and accessibility features. Extends all standard HTML textarea element props.
 * 
 * @component
 * @param {React.ComponentProps<"textarea">} props - All standard HTML textarea element props
 * @returns {JSX.Element} A styled textarea element
 * 
 * @example
 * ```tsx
 * <Textarea placeholder="Enter your message" rows={4} />
 * ```
 * 
 * @example
 * ```tsx
 * <Textarea value={message} onChange={(e) => setMessage(e.target.value)} />
 * ```
 */
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

export { Textarea };
